import type { ButtonProps as MuiButtonProps } from '@mui/material';
import { Button as MuiButton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { forwardRef } from 'react';

import { Neutrals, Primary, SemanticError } from '../../color-palette/colors';

import { InlineSpinner } from './InlineSpinner';
import { guardedClick, isAriaDisabled } from './softDisabled';

export type ButtonIntent = 'primary' | 'secondary' | 'tertiary' | 'danger';
export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonTone = 'default' | 'danger';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color' | 'size'> {
  intent?: ButtonIntent;
  size?: ButtonSize;
  tone?: ButtonTone;
  loading?: boolean;
  busy?: boolean;
}

const INTENT: Record<ButtonIntent, Pick<MuiButtonProps, 'variant' | 'color'>> = {
  primary: { variant: 'contained', color: 'primary' },
  secondary: { variant: 'outlined', color: 'inherit' },
  tertiary: { variant: 'text', color: 'inherit' },
  danger: { variant: 'contained', color: 'error' },
};

type AriaDisabled = MuiButtonProps['aria-disabled'];

interface Pending {
  readonly inFlight: boolean;
  readonly ariaDisabled: AriaDisabled;
}

const pendingOf = (loading: boolean, busy: boolean, ariaDisabled: AriaDisabled): Pending =>
  loading ? { inFlight: true, ariaDisabled: true } : { inFlight: busy, ariaDisabled };

const DANGER = SemanticError[600];
const SOFT_DISABLED = '&[aria-disabled="true"]';
const UNDERLINE_ON = '&:hover, &.Mui-focusVisible';

const baseSx: SystemStyleObject<Theme> = {
  whiteSpace: 'normal',
  textWrap: 'balance',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

const heightSx =
  (size: Exclude<ButtonSize, 'large'>) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    minHeight: theme.controlHeight[size],
    '@media (pointer: coarse)': { minHeight: theme.controlHeight.medium },
  });

const unavailableSx = (theme: Theme, contained: boolean, outlined: boolean): SystemStyleObject<Theme> => ({
  [SOFT_DISABLED]: {
    color: theme.palette.action.disabled,
    backgroundColor: contained ? theme.palette.action.disabledBackground : 'transparent',
    borderColor: outlined ? theme.palette.action.disabledBackground : undefined,
    cursor: 'not-allowed',
    boxShadow: 'none',
    '&:hover': { backgroundColor: contained ? theme.palette.action.disabledBackground : 'transparent' },
  },
});

const LOOK: Record<ButtonIntent, (theme: Theme, tone: ButtonTone) => SystemStyleObject<Theme>> = {
  primary: (theme) => unavailableSx(theme, true, false),
  danger: (theme) => ({
    backgroundColor: DANGER,
    color: theme.palette.common.white,
    '&:hover': { backgroundColor: SemanticError[900] },
    ...unavailableSx(theme, true, false),
  }),
  secondary: (theme, tone) => ({
    color: tone === 'danger' ? DANGER : Neutrals[900],
    borderColor: tone === 'danger' ? DANGER : Neutrals[500],
    '&:hover': { borderColor: tone === 'danger' ? DANGER : Neutrals[700] },
    ...unavailableSx(theme, false, true),
  }),
  tertiary: (theme, tone) => ({
    color: tone === 'danger' ? DANGER : Primary[600],
    paddingInline: 2,
    [UNDERLINE_ON]: { textDecoration: 'underline', textUnderlineOffset: theme.typography.underlineOffset },
    [SOFT_DISABLED]: {
      color: Neutrals[600],
      cursor: 'not-allowed',
      [UNDERLINE_ON]: { textDecoration: 'none', backgroundColor: 'transparent' },
    },
  }),
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { intent = 'primary', size = 'small', tone = 'default', loading = false, busy = false, disabled, startIcon, children, sx, onClick, 'aria-disabled': ariaDisabledProp, ...rest },
  ref,
) {
  const { inFlight, ariaDisabled } = pendingOf(loading, busy, ariaDisabledProp);
  const handleClick = guardedClick(inFlight || isAriaDisabled(ariaDisabled), onClick);

  return (
    <MuiButton
      ref={ref}
      {...INTENT[intent]}
      size={size}
      disabled={disabled}
      aria-disabled={ariaDisabled}
      aria-busy={inFlight || undefined}
      startIcon={inFlight ? <InlineSpinner /> : startIcon}
      onClick={handleClick}
      sx={[
        baseSx,
        (theme) => LOOK[intent](theme, tone),
        size !== 'large' && heightSx(size),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </MuiButton>
  );
});
